import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { OrderedMap, OrderedSet } from 'immutable';
import { EventSearchParameters } from '../models/event-search-params';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { Router } from '@angular/router';
import { EventSearchFilter } from '../models/event-filter';
import { EventFilterType } from '../models/event-filter-type-enum';
import { EventFilterUtilsService } from './event-filter-util-service';
import { EventMenuSearchFilter } from '../models/event-menu-search-filter';
import { EventMenuSearchFilterMapper } from '../../menus/event-search-filter/event-menu-search-filter-mapper';
import { LogService } from '../../shared/log/log.service';
import { SearchEventUtils } from '../utils/search-event-utils';
import { Constants } from '../../shared/constants';
import { AuthenticationStore } from '../../permission/services/authentication.store';

@Injectable()
export class EventSearchParametersStore {
  private searchTextSetSubject: BehaviorSubject<OrderedSet<string>> = new BehaviorSubject(OrderedSet([]));
  private searchFilterMapSubject: BehaviorSubject<OrderedMap<any, EventSearchFilter>> =
    new BehaviorSubject(OrderedMap<any, EventSearchFilter>());
  private defaultViewSubject: BehaviorSubject<boolean> = new BehaviorSubject(true);

  constructor(private environment: EnvironmentConfiguration,
              private router: Router,
              private eventFilterUtils: EventFilterUtilsService,
              private eventMenuMapper: EventMenuSearchFilterMapper,
              private logger: LogService,
              private authStore: AuthenticationStore) {
    this.setFilterSet(SearchEventUtils.convertStringToSearchFilterSet(this.getInitialFilterParams()));
  }
  get searchTextSet(): Observable<OrderedSet<string>> {
    return this.searchTextSetSubject.asObservable();
  }

  get searchFilterSet(): Observable<OrderedSet<EventSearchFilter>> {
    return this.searchFilterMapSubject.asObservable().map(map => map.toOrderedSet());
  }

  get searchFilterRecord(): Observable<EventMenuSearchFilter> {
    return this.searchFilterMapSubject.asObservable().map(map => this.eventMenuMapper.toEventMenuSearchFilter(map.toOrderedSet()));
  }

  get searchTextSetSnapshot(): OrderedSet<string> {
    return this.searchTextSetSubject.getValue();
  }

  get searchFilterSetSnapshot(): OrderedSet<EventSearchFilter> {
    return this.searchFilterMapSubject.getValue().toOrderedSet();
  }

  get eventSearchParametersSnapshot(): EventSearchParameters {
    return new EventSearchParameters({
      searchText: this.searchTextSetSnapshot,
      filters: this.searchFilterSetSnapshot,
      maxEvents: this.environment.events.maxEvents
    });
  }

  getInitialFilterParams(): string {
    if (this.authStore.hasPermission('EventSearch')) {
      return this.authStore.hasPermission('AgencyEventLevelIndicators') ?
        Constants.INITIAL_FILTER_PARAMS_FUll_AGENCY :
        Constants.INITIAL_FILTER_PARAMS;
    } else {
      return Constants.INITIAL_FILTER_PARAMS_NO_SEARCH;
    }
  }

  reset(updateUrl = false): void {
    const filterSet = SearchEventUtils.convertStringToSearchFilterSet(this.getInitialFilterParams());
    this.setFilterSet(filterSet, true);
    this.setSearchTextSetSubject(OrderedSet([]));
    if (updateUrl) {
      this.updateUrl();
    }
  }

  resetFilters(updateUrl = false): void {
    const filterSet = SearchEventUtils.convertStringToSearchFilterSet(this.getInitialFilterParams());
    this.setFilterSet(filterSet, true);
    if (updateUrl) {
      this.updateUrl();
    }
  }

  setSearchTextSet(searchTextSet: OrderedSet<string>, updateUrl = false): void {
    this.setSearchTextSetSubject(searchTextSet);
    if (updateUrl) {
      this.updateUrl();
    }
  }

  setFilterSet(filterSet: OrderedSet<EventSearchFilter>, updateUrl = false): void {
    let map = OrderedMap<any, EventSearchFilter>();
    filterSet.forEach((filter: EventSearchFilter) => {
      map = map.set(this.eventFilterUtils.getUniqueTokenKey(filter), filter);
    });

    this.setSearchFilterMapSubject(map);
    if (updateUrl) {
      this.updateUrl();
    }
  }

  addSearchText(searchText: string, updateUrl = false): void {
    this.setSearchTextSetSubject(this.searchTextSetSubject.getValue().add(searchText ? searchText.trim() : searchText));
    if (updateUrl) {
      this.updateUrl();
    }
  }

  addSearchFilter(filterField: string, filterValue: string, updateUrl = false): void {
    const filter = new EventSearchFilter({
      filterField: filterField,
      filterValue: filterValue ? filterValue.trim() : filterValue
    });

    this.setSearchFilterMapSubject(this.searchFilterMapSubject.getValue().set(
      this.eventFilterUtils.getUniqueTokenKey(filter), filter)
    );

    if (updateUrl) {
      this.updateUrl();
    }
  }

  removeSearchText(searchText: string, updateUrl = false): void {
    this.setSearchTextSetSubject(this.searchTextSetSubject.getValue().delete(searchText));
    if (updateUrl) {
      this.updateUrl();
    }
  }

  removeSearchFilterByFieldAndValue(filterField: string, filterValue: string, updateUrl = false): void {
    const filter = new EventSearchFilter({
      filterField: filterField,
      filterValue: filterValue ? filterValue.trim() : filterValue
    });
    this.removeSearchFilter(filter, updateUrl);
  }

  removeSearchFilter(filter: EventSearchFilter, updateUrl = false): void {
    if (filter.filterField === EventFilterType.PAYMENT_STATUS.toString() && this.countPaymentFilter() === 1) {
      return;
    }

    this.setSearchFilterMapSubject(this.searchFilterMapSubject.getValue().delete(
      this.eventFilterUtils.getUniqueTokenKey(filter))
    );

    if (updateUrl) {
      this.updateUrl();
    }
  }

  hasSearchFilter(filterField: string): boolean {
    let filters: OrderedSet<EventSearchFilter> = this.searchFilterSetSnapshot;
    let found = false;
    filters.map(filter => {
      if (filterField === filter.filterField) {
        found = true;
      }
    });
    return found;
  }

  hasSearchFilterByFieldAndValue(filterField: string, filterValue: string): boolean {
    let found = false;
    this.searchFilterSetSnapshot.map(filter => {
      if (filterField === filter.filterField && filterValue === filter.filterValue) {
        found = true;
      }
    });
    return found;
  }

  getQueryParams(): Object {

    const queryParams: Object = new Object();

    if (this.searchTextSetSnapshot
      && this.searchTextSetSnapshot.toArray()
      && this.searchTextSetSnapshot.toArray().length > 0) {
      queryParams[ 'search' ] = this.searchTextSetSnapshot.toArray();
    }

    if (this.searchFilterSetSnapshot
      && this.searchFilterSetSnapshot.toArray()
      && this.searchFilterSetSnapshot.toArray().length > 0) {
      queryParams[ 'filters' ] = JSON.stringify(this.searchFilterSetSnapshot.toArray());
    }

    return queryParams;
  }

  updateUrl(): void {
    if (this.isDefaultViewSelectedSnapshot) {
      this.defaultViewUrl();
    } else {
      this.financialViewUrl();
    }
  }

  defaultViewUrl(): void {
    this.logger.info('EventSearchParametersStore: Navigating to default');
    this.router.navigate([ '/events' ], {
      queryParams: this.getQueryParams()
    });
  }

  financialViewUrl(): void {
    this.logger.info('EventSearchParametersStore: Navigating to financial');
    this.router.navigate([ '/events/financial' ], {
      queryParams: this.getQueryParams()
    });
  }

  get isDefaultViewSelected(): Observable<boolean> {
    return this.defaultViewSubject.asObservable();
  }

  get isDefaultViewSelectedSnapshot(): boolean {
    return this.defaultViewSubject.getValue();
  }

  setDefaultViewSelected(showDefaultView: boolean): void {
    if (showDefaultView === null) {
      throw new Error('showDefaultView must be true or false');
    }
    this.logger.debug(`EventSearchParametersStore.setDefaultViewSelected: ${JSON.stringify(showDefaultView)}`);
    this.defaultViewSubject.next(showDefaultView);
  }

  setSearchTextSetSubject(value: OrderedSet<string>) {
    if (value === null ) {
      throw new Error('searchText must exist');
    }

    this.logger.debug(`EventSearchParametersStore.setSearchTextSetSubject: ${JSON.stringify(value)}`);
    this.searchTextSetSubject.next(value);
  }

  setSearchFilterMapSubject(value: OrderedMap<any, EventSearchFilter>) {
    if (value === null ) {
      throw new Error('searchFilter must exist');
    }
    this.logger.debug(`EventSearchParametersStore.setSearchFilterMapSubject: ${JSON.stringify(value)}`);
    this.searchFilterMapSubject.next(value);
  }

  private countPaymentFilter(): number {
    const paymentStatusFilters = this.searchFilterMapSubject.getValue().toList()
      .filter(filter => filter.filterField === EventFilterType.PAYMENT_STATUS.toString());
    return paymentStatusFilters.size;
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/service/event-search-parameters.store.ts